import { useAuth } from '@/services/auth';
import { AgentFileS3UploadStatus, getKnowledgeMappedDocuments } from '@/state/agent';
import { useAgentEditorStore } from '@/state/root';
import { rootStore } from '@/state/root/store';
import { AgentGrounding } from '@d-id/types';
import { runInAction } from 'mobx';
import { useCallback, useEffect, useMemo } from 'react';
import { useUpdateKnowledgeDocuments } from '../api';

const fetchKnowledgeDocumentsAndUpdateStore = async (knowledgeId: string) => {
    const { agentEditorStore } = rootStore;
    try {
        const docs = await getKnowledgeMappedDocuments(knowledgeId);

        runInAction(() => {
            agentEditorStore.resetDeletedDocumentFiles();
            agentEditorStore.setUploadingDocumentFiles(docs);
            agentEditorStore.setInitAgentState(prev => ({ ...prev, documents: docs }));
        });
    } catch (e) {
        console.error(`Failed to get updated documents:\n${e}`);
    }
};

const useUpdateKnowledgeDocumentsAndUpdateStore = () => {
    const agentEditorStore = useAgentEditorStore();
    const { mutateAsync: updateKnowledgeDocuments } = useUpdateKnowledgeDocuments();

    return useCallback(async () => {
        const {
            initAgentState: { knowledgeId },
            uploadingDocumentFiles,
            deletedDocumentFiles,
        } = agentEditorStore;

        if (!agentEditorStore.inputAgent.id) {
            console.warn('No agent id for knowledge update');
            return;
        }

        if (!knowledgeId) {
            console.warn('No knowledge id for documents update');
            return;
        }

        try {
            agentEditorStore.setIsUpdatingKnowledge(true);

            await updateKnowledgeDocuments({
                knowledgeId,
                uploadingFiles: uploadingDocumentFiles,
                deletedFiles: deletedDocumentFiles,
            });

            await fetchKnowledgeDocumentsAndUpdateStore(knowledgeId);
        } finally {
            agentEditorStore.setIsUpdatingKnowledge(false);
        }
    }, [updateKnowledgeDocuments]);
};

export const useUpdateKnowledgeDocumentsOnBackground = () => {
    const agentEditorStore = useAgentEditorStore();
    const updateKnowledgeDocuments = useUpdateKnowledgeDocumentsAndUpdateStore();

    const { isAnonymous } = useAuth();

    const isKnowledgeDisabled = useMemo(
        () => !!isAnonymous || agentEditorStore.llm.template === AgentGrounding.Ungrounded,
        [isAnonymous, agentEditorStore.llm.template]
    );

    const isKnowledgeChanged = useMemo(
        () =>
            agentEditorStore.uploadingDocumentFiles.some(file => !file.id) || agentEditorStore.deletedDocumentFiles.some(file => !!file.id),
        [agentEditorStore.uploadingDocumentFiles, agentEditorStore.deletedDocumentFiles]
    );

    const isUpdateKnowledgeDisabled = useMemo(() => {
        return (
            isKnowledgeDisabled ||
            !(
                agentEditorStore.uploadingDocumentFiles.length > 0 &&
                agentEditorStore.uploadingDocumentFiles.every(item => item.status === AgentFileS3UploadStatus.Validated) &&
                isKnowledgeChanged
            )
        );
    }, [isKnowledgeDisabled, agentEditorStore.uploadingDocumentFiles, isKnowledgeChanged]);

    useEffect(() => {
        if (!isUpdateKnowledgeDisabled && isKnowledgeChanged) {
            updateKnowledgeDocuments();
        }
    }, [isKnowledgeChanged, isUpdateKnowledgeDisabled]);
};
